import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import SearchIcon from "@mui/icons-material/Search";

import Header from "../../Header";
import Sidebar from "../../Sidebar";
import Footer from "../../Footer";
import Organization from "./Organization";
import { Organizationlist } from "../../../redux/actions/OrganizationAction";
import { UserDropdown } from "../../../redux/actions/userActions";

const Organizations = () => {
    const dispatch = useDispatch();
    const users = useSelector((s) => s.userDropdown.users);
    const [currentPage, setCurrentPage] = useState(0);
    const [user, setUser] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const role = useSelector((s) => s.userLogin.userInfo.data.role);
    const organizationList = useSelector((s) => s.organizationList);
    const { organizations, totalCount, sortBy } = organizationList;
    const [organizationsPerPage, setOrganizationsPerPage] = useState(20);

    const runList = (overrides = {}) => {
        const args = {
            pageNum: currentPage + 1,
            perPage: organizationsPerPage,
            sortBy: sortBy || "",
            searchTerm,
            user,
            ...overrides,
        };
        dispatch(
            Organizationlist(
                args.pageNum,
                args.perPage,
                args.sortBy,
                args.searchTerm,
                role,
                "user",
                args.user,
            ),
        );
    };

    useEffect(() => {
        runList({ pageNum: 1 });
        dispatch(UserDropdown("organization", ""));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationsPerPage]);

    const handlePageChange = (_event, newPage) => {
        setCurrentPage(newPage);
        runList({ pageNum: newPage + 1 });
    };

    const handleRowsPerPageChange = (e) => {
        setOrganizationsPerPage(Number(e.target.value));
        setCurrentPage(0);
    };

    const handleSearchKey = (e) => {
        if (e.key === "Enter") {
            setCurrentPage(0);
            runList({ searchTerm, pageNum: 1 });
        }
    };

    const handleFilterByUser = (e) => {
        const filterByUser = e.target.value;
        setUser(filterByUser);
        setCurrentPage(0);
        runList({ user: filterByUser, pageNum: 1 });
    };

    return (
        <>
            <div className="container-scroller">
                <Header />
                <div className="container-fluid page-body-wrapper">
                    <Sidebar />
                    <Box component="main" className="main-panel" sx={{ display: "flex", flexDirection: "column" }}>
                        <Box sx={{ flex: 1, py: { xs: 2, md: 3 } }}>
                            <Container maxWidth="xl" disableGutters sx={{ px: { xs: 2, md: 3 } }}>
                                <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "divider" }}>
                                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: 3 }}>
                                        <Typography variant="h3" component="h1">Organizations</Typography>
                                    </Stack>

                                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 2 }}>
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel id="user-filter-label">Filter by user</InputLabel>
                                            <Select
                                                labelId="user-filter-label"
                                                label="Filter by user"
                                                value={user}
                                                onChange={handleFilterByUser}
                                            >
                                                <MenuItem value="">All</MenuItem>
                                                {users?.map((u) => (
                                                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <TextField
                                            size="small"
                                            placeholder="Search organizations — press Enter"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onKeyDown={handleSearchKey}
                                            sx={{ flex: 1, minWidth: 240 }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Stack>

                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 12, background: "var(--ba-bg-soft, #FAFBFC)" } }}>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Email</TableCell>
                                                    <TableCell>Phone</TableCell>
                                                    <TableCell>Created By</TableCell>
                                                    <TableCell>Created Date</TableCell>
                                                    <TableCell>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {organizations?.length > 0 ? (
                                                    organizations.map((org) => (
                                                        <Organization org={org} key={org.id} />
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 6 }}>
                                                            No organizations yet.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <TablePagination
                                        component="div"
                                        count={totalCount || organizations?.length || 0}
                                        page={currentPage}
                                        onPageChange={handlePageChange}
                                        rowsPerPage={organizationsPerPage}
                                        onRowsPerPageChange={handleRowsPerPageChange}
                                        rowsPerPageOptions={[20, 40, 60, 80, 100]}
                                    />
                                </Paper>
                            </Container>
                        </Box>
                        <Footer />
                    </Box>
                </div>
            </div>
        </>
    );
};

export default Organizations;
